import React, {
  useCallback,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { autorun } from 'mobx';

import ConfirmDialog from '../../../../../components/ConfirmDialog';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import Table from '../../../../../../components/v2/Table';
import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import useToast from '../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import { PROMOTION_STATUS } from '../../../../../Model/Promotion';
import {
  InfoTag,
  DarkTag,
} from '../../../../../../components/Tags';

import getColumns from './columns';
import {
  ChipContainer,
  ActionsContainer,
  StyledEditIcon,
  StyledDeleteIcon,
} from './styles';
import texts from './texts';

const PromotionsTable = ({
  promotionDocs,
  onEdit,
}) => {
  const [selectedPromotionToDelete, setSelectedPromotionToDelete] = useState(null);
  const [activePromotions, setActivePromotions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  // Filter out deleted promotions
  useEffect(() => {
    const disposer = autorun(() => {
      const activePromotionsFiltered = promotionDocs.filter((doc) => !doc.isDeleted);
      setActivePromotions(activePromotionsFiltered);
    });

    return () => disposer();
  }, [promotionDocs]);

  const handleDeletePromotion = useCallback(async () => {
    setIsLoading(true);
    try {
      const promotion = promotionDocs.find((doc) => doc.id === selectedPromotionToDelete.id);
      await promotion.delete();
      showToast(texts.deleteSuccessful);
    } catch (error) {
      showToast(format(texts.deleteFailed, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setSelectedPromotionToDelete(null);
        setIsLoading(false);
      }
    }
  }, [
    promotionDocs,
    showToast,
    selectedPromotionToDelete,
    isComponentMountedRef,
  ]);

  const renderStatusCell = useCallback(
    ({ row }) => {
      const isOngoing = row.isOngoing();
      const promotionStatus = isOngoing ? PROMOTION_STATUS.ONGOING : PROMOTION_STATUS.UPCOMING;

      return (
        <ChipContainer>
          {promotionStatus === PROMOTION_STATUS.UPCOMING ? (
            <DarkTag>
              {texts.PromotionStatus[promotionStatus]}
            </DarkTag>
          ) : (
            <InfoTag>
              {texts.PromotionStatus[promotionStatus]}
            </InfoTag>
          )}
        </ChipContainer>
      );
    }, [],
  );

  const renderActionCell = useCallback(({ row }) => {
    const isOngoing = row.isOngoing() || false;

    return (
      <ActionsContainer>
        {!isOngoing && (
          <PrimaryButton
            onClick={() => onEdit(row.id)}
            icon={<StyledEditIcon />}
            variant="info"
            size="medium"
          >
            {texts.buttons.edit}
          </PrimaryButton>
        )}
        <PrimaryButton
          onClick={() => setSelectedPromotionToDelete(row)}
          icon={<StyledDeleteIcon />}
          variant="info"
          size="medium"
        >
          {texts.buttons.delete}
        </PrimaryButton>
      </ActionsContainer>
    );
  }, [
    onEdit,
    setSelectedPromotionToDelete,
  ]);

  const columns = getColumns({
    statusRenderCell: renderStatusCell,
    actionsRenderCell: renderActionCell,
  });

  const sortedPromotionDocs = activePromotions.sort((a, b) => a.startDate - b.startDate);

  return (
    <>
      <Table
        rows={sortedPromotionDocs}
        columns={columns}
        enableSearch
      />
      <ConfirmDialog
        isOpen={!!selectedPromotionToDelete}
        onCancel={() => setSelectedPromotionToDelete(null)}
        onConfirm={handleDeletePromotion}
        dialogTexts={{
          title: format(texts.deletePromotionConfirmation, { promotion: selectedPromotionToDelete?.description }),
        }}
      />
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

PromotionsTable.propTypes = {
  promotionDocs: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default PromotionsTable;
